import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export const CreateAccountDialog = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const queryClient = useQueryClient();

  const resetForm = () => {
    setName('');
    setDomain('');
    setLinkedinUrl('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;

    setLoading(true);
    const { error } = await supabase.from('companies').insert([{
      company_name: trimmedName,
      domain: domain.trim() || null,
      linkedin_url: linkedinUrl.trim() || null,
      account_tier: 'cold',
      is_icp_relevant: true,
    }]);
    setLoading(false);

    if (error) {
      toast.error('Failed to create account', { description: error.message });
      return;
    }

    toast.success(`Account "${trimmedName}" created`, { description: 'Status: to be processed' });
    queryClient.invalidateQueries({ queryKey: ['accounts'] });
    resetForm();
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <Plus className="h-4 w-4" />
          Create Account
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Account</DialogTitle>
          <DialogDescription>Add a new account stub for backend processing.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="ca-name">Company Name *</Label>
            <Input id="ca-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Acme Therapeutics" required maxLength={200} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ca-domain">Domain</Label>
            <Input id="ca-domain" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="e.g. acmetherapeutics.com" maxLength={200} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ca-linkedin">LinkedIn Company URL</Label>
            <Input id="ca-linkedin" value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} placeholder="e.g. https://linkedin.com/company/acme" maxLength={500} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={loading || !name.trim()}>
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Create
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
